import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Corregge il problema delle icone mancanti in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Added onCountryClick prop
const LeafletMapComponent = ({ selectedCountry, resetView, onCountryClick }) => {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const geoJsonLayerRef = useRef(null);
  const highlightedLayerRef = useRef(null);
  const markerRef = useRef(null); // Ref to store the marker
  
  // Inizializza la mappa
  useEffect(() => {
    // Crea la mappa solo una volta
    if (!leafletMapRef.current) {
      leafletMapRef.current = L.map(mapRef.current).setView([55, 15], 4);
      
      // Aggiungi il layer della mappa in bianco e nero
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
        maxZoom: 19,
      }).addTo(leafletMapRef.current);
      
      // Carica i dati GeoJSON dell'Europa
      fetch('https://raw.githubusercontent.com/leakyMirror/map-of-europe/master/GeoJSON/europe.geojson')
        .then(response => response.json())
        .then(data => {
          // Aggiungi il layer GeoJSON
          geoJsonLayerRef.current = L.geoJSON(data, {
            style: {
              weight: 1,
              color: '#666',
              fillColor: '#f8f9fa',
              fillOpacity: 0.7
            },
            // Add click listener to each feature
            onEachFeature: (feature, layer) => {
              layer.on({
                click: () => {
                  // Extract ISO code (prefer ISO3, fallback to iso_a3)
                  const isoCode = feature.properties.ISO3 || feature.properties.iso_a3;
                  if (isoCode && onCountryClick) {
                    onCountryClick(isoCode); // Call the handler passed from parent
                  }
                }
              });
            }
          }).addTo(leafletMapRef.current);
        })
        .catch(error => console.error('Errore caricamento GeoJSON:', error));
    }
    
    // Cleanup alla rimozione del componente
    return () => {
      if (leafletMapRef.current) {
        leafletMapRef.current.remove();
        leafletMapRef.current = null;
      }
    };
  }, []);
  
  // Gestisce i cambiamenti del paese selezionato
  useEffect(() => {
    if (!leafletMapRef.current) return;
    
    // Rimuovi l'evidenziazione e il marker precedenti
    if (highlightedLayerRef.current) {
      leafletMapRef.current.removeLayer(highlightedLayerRef.current);
      highlightedLayerRef.current = null;
    }
    if (markerRef.current) { // Remove previous marker
      leafletMapRef.current.removeLayer(markerRef.current);
      markerRef.current = null;
    }
    
    // Reset della vista se richiesto
    if (resetView) {
      leafletMapRef.current.setView([55, 15], 4);
      // Ensure marker is removed on reset
      if (markerRef.current) {
          leafletMapRef.current.removeLayer(markerRef.current);
          markerRef.current = null;
      }
      return;
    }
    
    // Se nessun paese è selezionato, non fare nulla
    if (!selectedCountry) return;
    
    // Carica i dati GeoJSON e evidenzia il paese selezionato
    fetch('https://raw.githubusercontent.com/leakyMirror/map-of-europe/master/GeoJSON/europe.geojson')
      .then(response => response.json())
      .then(data => {
        // Trova il feature del paese corrispondente
        const countryFeature = data.features.find(feature => {
          return (
            feature.properties.ISO3 === selectedCountry.iso ||
            feature.properties.iso_a3 === selectedCountry.iso
          );
        });
        
        if (countryFeature) {
          // Aggiungi il layer del paese evidenziato
          highlightedLayerRef.current = L.geoJSON(countryFeature, {
            style: {
              weight: 2,
              color: '#000',
              fillColor: '#3388ff',
              fillOpacity: 0.7
            }
          }).addTo(leafletMapRef.current);

          // Add marker at the centroid
          const center = highlightedLayerRef.current.getBounds().getCenter();
          markerRef.current = L.marker(center).addTo(leafletMapRef.current);
          
          // Zoom sul paese - RIMOSSO per non cambiare zoom/pan
          // leafletMapRef.current.fitBounds(highlightedLayerRef.current.getBounds()); 
        }
      })
      .catch(error => console.error('Errore nell\'evidenziare il paese:', error));
  }, [selectedCountry, resetView]);

  return <div ref={mapRef} style={{ height: '100%', width: '100%' }} />; // Changed height to 100%
};

export default LeafletMapComponent;
